/**
 * UnifiedBottomPanel — Shared floating bottom panel shell with tab system.
 *
 * Used by CharacterDesigner, LocationDesigner, ObjectDesigner, and ImageGenerator.
 * This component provides only the panel container, tab bar, and open/close animation.
 * Tab content is rendered via a children render function (slot pattern).
 *
 * Follows floating panel pattern: nopan nodrag nowheel, inverse-zoom scaling,
 * spring animation (stiffness 400, damping 25).
 */

import { memo, type ReactNode } from 'react'
import { useViewport } from 'reactflow'
import { motion, AnimatePresence } from 'framer-motion'
import { SlidersHorizontal, Wand2, LayoutGrid, Mic, type LucideIcon } from 'lucide-react'
import { cn } from '../../../../lib/utils'
import { useReadOnly } from '../../../../contexts/ReadOnlyContext'

// ---------------------------------------------------------------------------
// Tab configuration types & constants
// ---------------------------------------------------------------------------

export interface TabDefinition {
  id: string
  label: string
  icon: LucideIcon
}

/** CharacterDesigner: Config, Refine, Variants, Voice */
export const CHARACTER_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
  { id: 'voice', label: 'Voice', icon: Mic },
]

/** LocationDesigner: Config, Refine, Variants */
export const LOCATION_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

/** ObjectDesigner: Config, Refine, Variants */
export const OBJECT_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

/** ImageGenerator: Config, Refine, Variants */
export const IMAGE_GEN_TABS: TabDefinition[] = [
  { id: 'config', label: 'Config', icon: SlidersHorizontal },
  { id: 'refine', label: 'Refine', icon: Wand2 },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
]

/** Default tab when panel first opens */
export const DEFAULT_TAB = 'config'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface UnifiedBottomPanelProps {
  /** Tab definitions for this node type */
  tabs: TabDefinition[]
  /** Currently active tab ID */
  activeTab: string
  /** Tab switch callback */
  onTabChange: (tabId: string) => void
  /** Whether the panel is visible/open */
  isVisible: boolean
  /** Optional max-height override for the content area in px (default: 340px) */
  contentHeight?: number
  /** Optional minimum width (px) for non-variants tabs */
  minWidth?: number
  /** Render function receiving the active tab ID — slot pattern for tab content */
  children: (activeTab: string) => ReactNode
}

const UnifiedBottomPanel = memo(function UnifiedBottomPanel({
  tabs,
  activeTab,
  onTabChange,
  isVisible,
  contentHeight,
  minWidth,
  children,
}: UnifiedBottomPanelProps) {
  const readOnly = useReadOnly()
  const { zoom } = useViewport()
  // IMPORTANT: Variants tab uses scale=1 (no inverse-zoom) so variant thumbnails
  // stay proportional to the main image at all zoom levels. Do NOT unify this
  // with the other tabs — the size match is intentional UX. See commit 729cd581.
  const scale = activeTab === 'variants' ? 1 : Math.min(2, Math.max(0.5, 1 / zoom))

  const widthExpr = activeTab === 'variants'
    ? 'max(calc(100% + 120px), 632px)'
    : minWidth
      ? `max(calc(100% + 120px), ${minWidth}px)`
      : 'calc(100% + 120px)'

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale, x: '-50%' }}
          animate={{ opacity: 1, y: 0, scale, x: '-50%' }}
          exit={{ opacity: 0, y: -10, scale, x: '-50%' }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25,
            opacity: { duration: 0.15 },
            scale: { duration: 0 },
          }}
          className="absolute z-50 nopan nodrag nowheel"
          style={{
            top: '100%',
            left: '50%',
            marginTop: 8,
            transformOrigin: 'top center',
            width: widthExpr,
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          data-testid="unified-bottom-panel"
        >
          <div className="bg-surface-1 text-foreground border border-border rounded-xl shadow-2xl overflow-hidden">
            {/* Tab Bar */}
            <div className="flex gap-1 p-2 border-b border-border-subtle">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => onTabChange(tab.id)}
                    className={cn(
                      'flex-1 flex flex-col items-center gap-0.5 px-3 py-1.5 text-xs font-medium rounded-lg relative transition-all duration-200',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon size={14} data-testid={`tab-icon-${tab.id}`} />
                    {tab.label}
                  </button>
                )
              })}
            </div>

            {/* Tab Content — auto height with upper boundary, overridable via contentHeight */}
            <div
              className={cn(
                'overflow-y-auto nowheel',
                readOnly && 'pointer-events-none select-text opacity-70',
              )}
              style={{ maxHeight: contentHeight ? `${contentHeight}px` : '340px' }}
              data-testid="tab-content-area"
            >
              {children(activeTab)}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
})

UnifiedBottomPanel.displayName = 'UnifiedBottomPanel'

export { UnifiedBottomPanel }
